import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";

import Menu from "./Menu";

const NAVBAR_API_URL = "https://admin.monsinidress.com/api/navbar";

const BASE_NAV_DATA = {
	Prom: { years: [2027, 2026] },
	Evening: { years: [2027, 2026] },
	Bridal: { years: [2027, 2026] },
};

const CATEGORY_ORDER = ["Prom", "Evening", "Bridal"];
let navbarDataCache = null;

function getCategorySlug(categoryName = "") {
	const category = String(categoryName).trim().toLowerCase();
	if (category.includes("prom")) return "prom";
	if (category.includes("evening")) return "evening";
	if (category.includes("bridal")) return "bridal";
	return category
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/-+/g, "-");
}

function getCanonicalCategory(categoryName = "") {
	const slug = getCategorySlug(categoryName);
	if (slug === "prom") return "Prom";
	if (slug === "evening") return "Evening";
	if (slug === "bridal") return "Bridal";
	return null;
}

function formatTitle(category) {
	if (category === "Evening") return "Enchanted Evening";
	if (category === "Bridal") return "Enchanted Bridal";
	return "Prom";
}

function normalizeNavbarData(apiData) {
	const normalizedData = {
		Prom: { years: [] },
		Evening: { years: [] },
		Bridal: { years: [] },
	};

	if (!apiData || typeof apiData !== "object" || Array.isArray(apiData)) {
		return BASE_NAV_DATA;
	}

	Object.entries(apiData).forEach(([categoryName, categoryData]) => {
		const canonicalCategory = getCanonicalCategory(categoryName);
		if (!canonicalCategory) return;

		const years = Array.isArray(categoryData?.years)
			? [...new Set(categoryData.years)].sort(
					(a, b) => Number(b) - Number(a)
			  )
			: [];

		normalizedData[canonicalCategory] = {
			years: years.length > 0 ? years : BASE_NAV_DATA[canonicalCategory].years,
		};
	});

	return normalizedData;
}

const ChevronDownIcon = ({ className = "ml-1 h-5 w-5" }) => (
	<svg
		viewBox="0 0 20 20"
		fill="currentColor"
		className={className}
		aria-hidden="true"
		focusable="false"
	>
		<path
			fillRule="evenodd"
			d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
			clipRule="evenodd"
		/>
	</svg>
);

const MenuIcon = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="h-7 w-7 text-gray-800"
		aria-hidden="true"
		focusable="false"
	>
		<path d="M4 6h16M4 12h16M4 18h16" />
	</svg>
);

const SearchIcon = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="h-6 w-6 text-black"
		aria-hidden="true"
		focusable="false"
	>
		<circle cx="11" cy="11" r="8" />
		<line x1="21" y1="21" x2="16.65" y2="16.65" />
	</svg>
);

const DropdownMenu = memo(function DropdownMenu({ title, options }) {
	return (
		<li className="group relative font-lora text-[15px] font-medium text-gray-800 xl:text-[18px]">
			<button
				type="button"
				className="flex items-center rounded px-1 py-2 text-gray-800 transition hover:text-pink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-700 focus-visible:ring-offset-2 xl:px-3"
				aria-haspopup="true"
			>
				{title}
				<ChevronDownIcon className="ml-1 h-5 w-5 transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
			</button>

			<div className="invisible absolute left-1/2 top-full z-40 w-52 -translate-x-1/2 rounded-md bg-white py-1 opacity-0 shadow-lg ring-1 ring-black/10 transition duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
				{options.map((option) => (
					<Link
						key={option.value}
						to={option.value}
						aria-label={option.ariaLabel}
						className="block w-full px-4 py-2 text-center text-sm text-gray-800 transition hover:bg-gray-100 hover:text-pink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pink-700"
					>
						{option.label}
					</Link>
				))}
			</div>
		</li>
	);
});

function NavLg({ menu, setMenu, onOpenSearch, isScrollingUp }) {
	const [data, setData] = useState(navbarDataCache || BASE_NAV_DATA);
	const requestStartedRef = useRef(Boolean(navbarDataCache));
	const controllerRef = useRef(null);

	useEffect(() => {
		return () => controllerRef.current?.abort();
	}, []);

	const loadNavbarData = useCallback(async () => {
		if (navbarDataCache) {
			setData(navbarDataCache);
			return;
		}

		if (requestStartedRef.current) return;
		requestStartedRef.current = true;

		const controller = new AbortController();
		controllerRef.current = controller;
		const timeoutId = window.setTimeout(() => controller.abort(), 6000);

		try {
			const response = await fetch(NAVBAR_API_URL, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ brand: "Monsini" }),
				signal: controller.signal,
			});

			if (!response.ok) {
				throw new Error(`Navbar request failed: ${response.status}`);
			}

			const result = await response.json();
			navbarDataCache = normalizeNavbarData(result?.data);
			setData(navbarDataCache);
		} catch (error) {
			if (error.name !== "AbortError") {
				setData(BASE_NAV_DATA);
			}
		} finally {
			window.clearTimeout(timeoutId);
		}
	}, []);

	const dropdownItems = useMemo(
		() =>
			CATEGORY_ORDER.map((category) => {
				const categorySlug = getCategorySlug(category);
				const years = data[category]?.years || [];
				const title = formatTitle(category);

				return {
					category,
					title,
					options: [
						...years.map((year) => ({
							label: `${year} Collection`,
							ariaLabel: `${year} ${title} Collection`,
							value: `/${categorySlug}/${year}`,
						})),
						{
							label: "Size Chart",
							ariaLabel: `${title} Size Chart`,
							value: `/size-chart/${categorySlug}`,
						},
					],
				};
			}),
		[data]
	);

	const handleMenuToggle = useCallback(() => {
		loadNavbarData();
		setMenu((currentMenu) => !currentMenu);
	}, [loadNavbarData, setMenu]);

	return (
		<nav
			aria-label="Primary navigation"
			className={`fixed left-0 top-0 z-30 flex w-full items-center justify-between border-b border-gray-100 bg-white px-4 py-[1.7rem] shadow-md transition-transform duration-300 md:px-8 md:py-7 lg:px-7 lg:py-5 xl:px-12 ${
				isScrollingUp ? "translate-y-0" : "-translate-y-full"
			}`}
		>
			<button
				type="button"
				onClick={handleMenuToggle}
				className="rounded p-1 lg:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-700 focus-visible:ring-offset-2"
				aria-label={menu ? "Close mobile menu" : "Open mobile menu"}
				aria-expanded={Boolean(menu)}
			>
				<MenuIcon />
			</button>

			<Link
				to="/"
				className="relative h-fit w-fit rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-700 focus-visible:ring-offset-2"
				aria-label="Monsini home page"
			>
				<div className="myclass relative text-5xl font-medium leading-tight tracking-tight text-gray-800">
					Monsini
					<span className="absolute bottom-0 right-0 whitespace-nowrap font-poppins text-[10px] font-bold leading-3 tracking-[0.01px] text-red-700">
						PROUDLY CANADIAN
					</span>
				</div>
			</Link>

			<ul
				className="hidden grow items-center justify-center gap-1 py-4 font-lora text-gray-800 lg:flex"
				onMouseEnter={loadNavbarData}
				onFocus={loadNavbarData}
			>
				<li className="p-2 text-[18px] font-medium">
					<Link to="/" className="rounded transition hover:text-pink-700">
						Home
					</Link>
				</li>

				{dropdownItems.map((item) => (
					<DropdownMenu
						key={item.category}
						title={item.title}
						options={item.options}
					/>
				))}

				<li className="p-2 text-[18px] font-medium">
					<Link to="/about" className="rounded transition hover:text-pink-700">
						About Us
					</Link>
				</li>

				<li className="p-2 text-[18px] font-medium">
					<Link to="/contact" className="rounded transition hover:text-pink-700">
						Contact Us
					</Link>
				</li>

				<li className="p-2">
					<a
						href="https://market.monsiniprom.com/"
						target="_blank"
						rel="noopener noreferrer"
						className="rounded-md border border-pink-700 px-4 py-1.5 font-bold text-pink-700 transition hover:border-pink-800 hover:text-pink-800"
					>
						Retailer Login
					</a>
				</li>
			</ul>

			<button
				type="button"
				onClick={onOpenSearch}
				className="rounded-full p-2 transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-700 focus-visible:ring-offset-2"
				aria-label="Open product search"
			>
				<SearchIcon />
			</button>

			<Menu menu={menu} setMenu={setMenu} items={dropdownItems} />
		</nav>
	);
}

export default memo(NavLg);
